// AddDocumentForm — "Add a letter or document…" at the case level (RRA v2.1 / W3,
// consensus A3 + A10; spec §3.W3.7).
//
// Staff file something the requester or a verifier sent outside the forms: a letter
// from someone who knows them, a signed permission to contact someone (A10: Jennifer
// Esparza's Adobe document), or another document. TWO steps, on purpose:
//
//   1. UPLOAD — the PDF goes into quarantine (stored, validated as a real PDF, never
//      read). The reply is the document's index on the case.
//   2. For a signed permission only — RECORD who signed, when, whether they signed
//      for themselves or as a parent or guardian, and the people it names (1 to 5).
//      That records the permission grant, and the verifier then receives THAT
//      document as their copy.
//
// `existingIndex` jumps straight to step 2 for a signed permission that was filed
// earlier without the follow-up. No free-text field except names, a role and
// contact details; nothing here can hold a diagnosis.

import { useState } from 'react';

import {
  recordRraPermissionFromDocument,
  rraErrorMessage,
  uploadRraStaffDocument,
  type RraStaffDocumentKind,
} from '@/lib/rraStaffClient';
import {
  blankPermissionDraft,
  blankPermissionPerson,
  DOCUMENT_KIND_OPTIONS,
  MAX_PERMISSION_PEOPLE,
  pdfFileError,
  PERMISSION_SIGNER_OPTIONS,
  permissionBody,
  permissionError,
  todayYmd,
  type PermissionDraft,
} from '@/lib/rraVerificationUi';

const FILE_LABEL = 'The PDF'; // DRAFT
const FILE_HELPER = 'It is stored with the case and not read.'; // DRAFT
const NOT_NOW = 'Not now'; // DRAFT
const SAVED_NOTICE = 'Document added.'; // DRAFT
const PERMISSION_SAVED_NOTICE = 'Signed permission recorded.'; // DRAFT
const PERMISSION_LATER_NOTICE =
  'Document added. Use “Record who it names” beside it when you are ready.'; // DRAFT

export function AddDocumentForm({
  caseId,
  existingIndex,
  onFinished,
  onCancel,
}: {
  caseId: string;
  /** A signed permission already filed: go straight to "who signed it". */
  existingIndex?: number;
  /** Called when the form is done (or set aside); the page reloads and shows the notice. */
  onFinished: (notice: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<RraStaffDocumentKind | ''>(existingIndex !== undefined ? 'signed_permission' : '');
  const [file, setFile] = useState<File | null>(null);
  const [documentIndex, setDocumentIndex] = useState<number | null>(existingIndex ?? null);
  const [draft, setDraft] = useState<PermissionDraft>(() => blankPermissionDraft());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 2 once a signed permission is stored (just now, or earlier).
  const permissionStep = kind === 'signed_permission' && documentIndex !== null;

  async function upload() {
    setError(null);
    if (kind === '') return setError('Please choose what it is.'); // DRAFT
    const fileProblem = pdfFileError(file);
    if (fileProblem) return setError(fileProblem);
    setBusy(true);
    try {
      const res = await uploadRraStaffDocument(caseId, file!, kind);
      if (kind === 'signed_permission') {
        setDocumentIndex(res.document.index);
      } else {
        await onFinished(SAVED_NOTICE);
      }
    } catch (err) {
      setError(rraErrorMessage(err, 'The document could not be added.'));
    } finally {
      setBusy(false);
    }
  }

  async function savePermission() {
    setError(null);
    const problem = permissionError(draft);
    if (problem) return setError(problem);
    const body = permissionBody(draft);
    if (!body || documentIndex === null) return setError('Please complete the form.');
    setBusy(true);
    try {
      await recordRraPermissionFromDocument(caseId, documentIndex, body);
      await onFinished(PERMISSION_SAVED_NOTICE);
    } catch (err) {
      setError(rraErrorMessage(err, 'The permission could not be recorded.'));
    } finally {
      setBusy(false);
    }
  }

  function setPerson(i: number, patch: Partial<PermissionDraft['people'][number]>) {
    setDraft({ ...draft, people: draft.people.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  }

  return (
    <div className="card" style={{ background: 'var(--surface-2, transparent)', marginTop: 'var(--space-2)' }}>
      <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
        {permissionStep ? 'Record who signed it' : 'Add a letter or document'}
      </div>

      {!permissionStep ? (
        <>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }} disabled={busy}>
            <legend className="label">What is it?</legend>
            <div className="stack-sm">
              {DOCUMENT_KIND_OPTIONS.map((o) => (
                <label key={o.value} className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" name="staff-doc-kind" checked={kind === o.value} onChange={() => setKind(o.value)} />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="field">
            <label className="label" htmlFor="staff-doc-file">{FILE_LABEL}</label>
            <input
              id="staff-doc-file"
              type="file"
              className="input"
              accept="application/pdf,.pdf"
              disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>{FILE_HELPER}</div>
          </div>
        </>
      ) : (
        <>
          <div className="field">
            <label className="label" htmlFor="perm-signed-name">Who signed it</label>
            <input
              id="perm-signed-name"
              className="input"
              maxLength={200}
              value={draft.signedName}
              disabled={busy}
              onChange={(e) => setDraft({ ...draft, signedName: e.target.value })}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="perm-signed-on">Date signed</label>
            <input
              id="perm-signed-on"
              type="date"
              className="input"
              max={todayYmd()}
              value={draft.signedOn}
              disabled={busy}
              onChange={(e) => setDraft({ ...draft, signedOn: e.target.value })}
            />
          </div>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }} disabled={busy}>
            <legend className="label">Who signed, relative to the person with the disability</legend>
            <div className="stack-sm">
              {PERMISSION_SIGNER_OPTIONS.map((o) => (
                <label key={o.value} className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="perm-signer"
                    checked={draft.signer === o.value}
                    onChange={() => setDraft({ ...draft, signer: o.value })}
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="label" style={{ marginTop: 'var(--space-2)' }}>The people it names</div>
          {draft.people.map((p, i) => (
            <div key={i} className="card" style={{ marginBottom: 'var(--space-2)' }}>
              <div className="field">
                <label className="label" htmlFor={`perm-name-${i}`}>Name</label>
                <input id={`perm-name-${i}`} className="input" maxLength={200} value={p.name} disabled={busy} onChange={(e) => setPerson(i, { name: e.target.value })} />
              </div>
              <div className="field">
                <label className="label" htmlFor={`perm-role-${i}`}>How they know the person (optional)</label>
                <input id={`perm-role-${i}`} className="input" maxLength={200} value={p.role} disabled={busy} onChange={(e) => setPerson(i, { role: e.target.value })} />
              </div>
              <div className="field">
                <label className="label" htmlFor={`perm-email-${i}`}>Email (optional)</label>
                <input id={`perm-email-${i}`} className="input" type="email" maxLength={320} value={p.email} disabled={busy} onChange={(e) => setPerson(i, { email: e.target.value })} />
              </div>
              <div className="field">
                <label className="label" htmlFor={`perm-phone-${i}`}>Phone (optional)</label>
                <input id={`perm-phone-${i}`} className="input" maxLength={50} value={p.phone} disabled={busy} onChange={(e) => setPerson(i, { phone: e.target.value })} />
              </div>
              {draft.people.length > 1 && (
                <button
                  type="button"
                  className="btn"
                  data-variant="ghost"
                  data-size="xs"
                  disabled={busy}
                  onClick={() => setDraft({ ...draft, people: draft.people.filter((_, j) => j !== i) })}
                >
                  Remove this person
                </button>
              )}
            </div>
          ))}
          {draft.people.length < MAX_PERMISSION_PEOPLE && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy}
              onClick={() => setDraft({ ...draft, people: [...draft.people, blankPermissionPerson()] })}
            >
              Add another person
            </button>
          )}
        </>
      )}

      {error && (
        <div className="alert" data-tone="danger" role="alert" style={{ margin: 'var(--space-2) 0' }}>
          <div className="alert-body">{error}</div>
        </div>
      )}
      <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        {!permissionStep ? (
          <button type="button" className="btn" data-variant="primary" data-size="sm" disabled={busy} onClick={() => void upload()}>
            {busy ? 'Adding…' : 'Add the document'}
          </button>
        ) : (
          <button type="button" className="btn" data-variant="primary" data-size="sm" disabled={busy} onClick={() => void savePermission()}>
            {busy ? 'Recording…' : 'Record the permission'}
          </button>
        )}
        {permissionStep ? (
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            disabled={busy}
            onClick={() => (existingIndex !== undefined ? onCancel() : void onFinished(PERMISSION_LATER_NOTICE))}
          >
            {NOT_NOW}
          </button>
        ) : (
          <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
